import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { CsrWebApiService } from '../../../../csr-web-api.service';
import { CareerChallengeJudgeDto } from '../../../../models';
import { CareerChallengeAdminStore } from '../shared/career-challenge-admin.store';
import { ImageUrlFieldComponent } from '../../../../shared/challenge-media/image-url-field.component';
import { resolveChallengeMediaSrc } from '../../../../shared/challenge-media/challenge-media.util';

@Component({
  selector: 'app-career-challenge-judges-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatCardModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatSnackBarModule,
    ImageUrlFieldComponent
  ],
  templateUrl: './judges-tab.component.html',
  styleUrl: './tab-shared.scss'
})
export class CareerChallengeJudgesTabComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(CsrWebApiService);
  private readonly store = inject(CareerChallengeAdminStore);
  private readonly fb = inject(FormBuilder);
  private readonly snackBar = inject(MatSnackBar);

  busy = false;
  editingId: string | null = null;
  editingDisplayUrl: string | null = null;

  readonly form = this.fb.nonNullable.group({
    name: ['', Validators.required],
    qualification: [''],
    summary: [''],
    photoUrl: [''],
    sortOrder: [0, Validators.required]
  });

  get id(): string {
    return this.route.parent?.snapshot.paramMap.get('id') ?? '';
  }

  get judges(): CareerChallengeJudgeDto[] {
    return this.store.challenge()?.judges ?? [];
  }

  get loading() {
    return this.store.loading();
  }

  ngOnInit(): void {
    void this.store.load(this.id);
  }

  edit(judge: CareerChallengeJudgeDto): void {
    this.editingId = judge.id;
    this.form.patchValue({
      name: judge.name,
      qualification: judge.qualification ?? '',
      summary: judge.summary ?? '',
      photoUrl: judge.photoUrl ?? '',
      sortOrder: judge.sortOrder
    });
    this.editingDisplayUrl = judge.photoDisplayUrl ?? null;
  }

  reset(): void {
    this.editingId = null;
    this.editingDisplayUrl = null;
    this.form.reset({ name: '', qualification: '', summary: '', photoUrl: '', sortOrder: 0 });
  }

  async save(): Promise<void> {
    if (this.form.invalid) {
      return;
    }
    this.busy = true;
    try {
      const raw = this.form.getRawValue();
      await firstValueFrom(
        this.api.upsertCareerChallengeJudge(
          this.id,
          {
            name: raw.name.trim(),
            qualification: raw.qualification.trim(),
            summary: raw.summary.trim(),
            photoUrl: raw.photoUrl.trim() || null,
            sortOrder: Number(raw.sortOrder) || 0
          },
          this.editingId ?? undefined
        )
      );
      this.snackBar.open('Judge saved', 'Close', { duration: 2200 });
      this.reset();
      await this.store.load(this.id, true);
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.busy = false;
    }
  }

  async remove(judgeId: string): Promise<void> {
    if (!confirm('Delete this judge?')) {
      return;
    }
    this.busy = true;
    try {
      await firstValueFrom(this.api.deleteCareerChallengeJudge(this.id, judgeId));
      await this.store.load(this.id, true);
    } catch (error) {
      this.snackBar.open(this.readError(error), 'Close', { duration: 3500 });
    } finally {
      this.busy = false;
    }
  }

  private readError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const detail = error.error?.detail ?? error.error?.title ?? error.error;
      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }
    }
    return 'Request failed';
  }

  judgeImageSrc(judge: CareerChallengeJudgeDto): string {
    return resolveChallengeMediaSrc(judge.photoDisplayUrl, judge.photoUrl, 'judge');
  }
}